import { Injectable } from '@angular/core';

export interface IconConfig {
  name: string;
  size?: 'sm' | 'base' | 'lg' | 'xl' | '2xl' | number;
  color?: string;
  weight?: 'light' | 'normal' | 'medium' | 'semibold' | 'bold' | 'extrabold';
  filled?: boolean;
  strokeWidth?: number;
}

@Injectable({
  providedIn: 'root'
})
export class IconService {

  private readonly sizeMap = {
    sm: '1rem',
    base: '1.25rem',
    lg: '1.5rem',
    xl: '2rem',
    '2xl': '2.5rem'
  };

  private readonly weightMap = {
    light: '300',
    normal: '400',
    medium: '500',
    semibold: '600',
    bold: '700',
    extrabold: '800'
  };

  /**
   * Genera la configuración de estilos para un icono
   * @param config Configuración del icono
   * @returns Objeto con estilos CSS
   */
  getIconStyles(config: IconConfig): { [key: string]: string } {
    const styles: { [key: string]: string } = {};

    // Tamaño
    if (config.size) {
      if (typeof config.size === 'number') {
        styles['width'] = `${config.size}px`;
        styles['height'] = `${config.size}px`;
      } else {
        const size = this.sizeMap[config.size] || this.sizeMap.base;
        styles['width'] = size;
        styles['height'] = size;
      }
    }

    // Color
    if (config.color) {
      styles['color'] = config.color;
    }

    // Peso de fuente (para iconos de texto)
    if (config.weight) {
      styles['font-weight'] = this.weightMap[config.weight];
    }

    // Stroke width para iconos SVG
    if (config.strokeWidth) {
      styles['stroke-width'] = config.strokeWidth.toString();
    }

    return styles;
  }

  /**
   * Obtiene las clases CSS para un icono
   * @param config Configuración del icono
   * @returns Array de clases CSS
   */
  getIconClasses(config: IconConfig): string[] {
    const classes: string[] = ['lucide-icon'];

    // Clase de tamaño
    if (config.size && typeof config.size === 'string') {
      classes.push(`icon-${config.size}`);
    }

    // Clase de peso
    if (config.weight) {
      classes.push(`icon-${config.weight}`);
    }

    // Clase de color
    if (config.color) {
      classes.push(`icon-color-${config.color.replace('#', '')}`);
    }

    // Clase de relleno
    if (config.filled) {
      classes.push('icon-filled');
    }

    return classes;
  }

  /**
   * Valida si un nombre de icono es válido
   * @param iconName Nombre del icono
   * @returns true si es válido
   */
  isValidIconName(iconName: string): boolean {
    // Lista de iconos comunes de Lucide (puedes expandir esta lista)
    const validIcons = [
      'home', 'user', 'settings', 'search', 'menu', 'close', 'edit', 'delete',
      'save', 'cancel', 'add', 'remove', 'arrow-left', 'arrow-right', 'arrow-up', 'arrow-down',
      'check', 'x', 'plus', 'minus', 'star', 'heart', 'eye', 'eye-off', 'lock', 'unlock',
      'mail', 'phone', 'calendar', 'clock', 'map-pin', 'link', 'download', 'upload',
      'file', 'folder', 'image', 'video', 'music', 'play', 'pause',
      'volume', 'volume-off', 'wifi', 'bluetooth', 'battery', 'signal',
      'sun', 'moon', 'cloud', 'rain', 'snow', 'wind', 'thermometer',
      'shopping-cart', 'credit-card', 'dollar-sign', 'euro', 'pound',
      'github', 'twitter', 'facebook', 'instagram', 'linkedin', 'youtube', 'panel-left', 'panel-right'
    ];

    return validIcons.includes(iconName.toLowerCase());
  }
}

